import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, Calendar, ArrowRight, Loader2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { servicesAPI, bookingsAPI } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    preferred_date: '',
    preferred_time: '',
    notes: '',
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      if (response.data && response.data.length > 0) {
        setServices(response.data);
      } else {
        // Use default services if none in DB
        setServices([
          {
            id: "svc_001",
            name: "1:1 Wellness Consultation",
            duration: "60 minutes",
            price: 89,
            description: "Personalized wellness assessment and herbal protocol tailored to your unique needs.",
            includes: ["Health Assessment", "Custom Protocol", "Product Recommendations", "Follow-up Support"]
          },
          {
            id: "svc_002",
            name: "Herbal Coaching Package",
            duration: "4 weeks",
            price: 249,
            description: "Comprehensive 4-week program with weekly sessions and ongoing support.",
            includes: ["4 Weekly Sessions", "Personalized Plan", "Email Support", "Resource Library Access"]
          },
          {
            id: "svc_003",
            name: "Group Wellness Workshop",
            duration: "2 hours",
            price: 45,
            description: "Interactive group sessions covering various wellness topics and herbal education.",
            includes: ["Live Workshop", "Q&A Session", "Take-Home Materials", "Community Access"]
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    
    try {
      await bookingsAPI.create({
        service_name: selectedService.name,
        service_price: selectedService.price,
        ...bookingForm,
      });
      setBookingSuccess(true);
      setTimeout(() => {
        setSelectedService(null);
        setBookingSuccess(false);
        setBookingForm({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          preferred_date: '',
          preferred_time: '',
          notes: '',
        });
      }, 3000);
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setBookingForm({ ...bookingForm, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-emerald-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-white mb-4">Wellness Services</h1>
          <p className="text-xl text-emerald-200 max-w-2xl">
            Personalized guidance and support on your journey to natural wellness.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={service.id}
                className={`bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all ${
                  idx === 1 ? 'lg:scale-105 ring-2 ring-emerald-500' : ''
                }`}
              >
                {idx === 1 && (
                  <div className="bg-emerald-600 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h3>
                  <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {service.duration}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-900 mb-3">What&apos;s Included:</p>
                    <ul className="space-y-2">
                      {service.includes?.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-6 border-t">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-3xl font-bold text-emerald-600">${service.price}</span>
                        {service.duration?.includes('week') && (
                          <span className="text-gray-500 text-sm">/package</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => setSelectedService(service)}
                      className={`w-full rounded-full ${
                        idx === 1
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to start your wellness journey.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Book a Session', desc: 'Choose the service that fits your needs and schedule a time.' },
              { step: '02', title: 'Initial Assessment', desc: 'We\'ll discuss your health goals and current wellness status.' },
              { step: '03', title: 'Custom Protocol', desc: 'Receive a personalized herbal and wellness plan.' },
              { step: '04', title: 'Ongoing Support', desc: 'Get continued guidance as you progress on your journey.' },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-600">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
                {idx < 3 && (
                  <ArrowRight className="w-6 h-6 text-emerald-300 mx-auto mt-4 hidden md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Book {selectedService?.name}</DialogTitle>
          </DialogHeader>
          
          {bookingSuccess ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Booking Submitted!</h3>
              <p className="text-gray-600">We&apos;ll confirm your appointment shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleBooking} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={bookingForm.customer_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="customer_email"
                  value={bookingForm.customer_email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={bookingForm.customer_phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date *</label>
                  <input
                    type="date"
                    name="preferred_date"
                    value={bookingForm.preferred_date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time *</label>
                  <select
                    name="preferred_time"
                    value={bookingForm.preferred_time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select time</option>
                    <option value="9:00 AM">9:00 AM</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={bookingForm.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  placeholder="Any specific concerns or goals you'd like to discuss?"
                />
              </div>
              
              <div className="pt-4 border-t flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Service Price</p>
                  <p className="text-2xl font-bold text-emerald-600">${selectedService?.price}</p>
                </div>
                <Button
                  type="submit"
                  disabled={bookingLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                >
                  {bookingLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Submit Booking'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
